export const convertUsdcToCad = (
  usdcAmount: number,
  cadUsdcRate?: number,
  options?: {
    includeFees?: number
    round?: boolean
    fallbackRate?: number
  }
): number | null => {
  if (!usdcAmount || isNaN(usdcAmount)) return null

  let exchangeRate = cadUsdcRate

  // Use fallback rate if no exchange rate provided
  if (!exchangeRate && options?.fallbackRate) {
    exchangeRate = options.fallbackRate
  }

  if (!exchangeRate) return null

  // cadUsdcRate from API = CAD per 1 USDC (e.g. 1.33 means 1 USDC = 1.33 CAD)
  let cadAmount = usdcAmount * exchangeRate

  // Add fees if specified
  if (options?.includeFees) {
    cadAmount += options.includeFees
  }

  // Round if specified
  if (options?.round) {
    cadAmount = Math.round(cadAmount)
  }

  return cadAmount
}

// Utility function specifically for error message formatting (returns string)
export const convertUsdcToCadForErrorMessages = (
  usdcAmount: number,
  cadUsdcRate?: number,
  cadFee: number = 0
): string => {
  const convertedAmount = convertUsdcToCad(usdcAmount, cadUsdcRate, {
    includeFees: cadFee,
    round: true,
    fallbackRate: 1.38, // Hardcoded fallback for error messages
  })
  return convertedAmount?.toString() || '0'
}

export const cadToDestinationRate = (
  cadPerUsdc: number,
  destinationPerUsdc: number
): number => destinationPerUsdc / cadPerUsdc

type CalculateExchangeRateParams = {
  sendAmount: string | number
  destinationAmount: string | number
  fallbackRate?: number
  cadUsdcRate?: number
  cpnExchangeRate?: number
}

export const calculateExchangeRate = ({
  sendAmount,
  destinationAmount,
  fallbackRate,
  cadUsdcRate,
  cpnExchangeRate,
}: CalculateExchangeRateParams): number => {
  if (cadUsdcRate && cpnExchangeRate && cadUsdcRate > 0) {
    return cadToDestinationRate(cadUsdcRate, cpnExchangeRate)
  }

  const send =
    typeof sendAmount === 'string' ? parseFloat(sendAmount) : sendAmount
  const destination =
    typeof destinationAmount === 'string'
      ? parseFloat(destinationAmount)
      : destinationAmount

  if (send > 0 && destination > 0) {
    return destination / send
  }

  return fallbackRate || 0
}

// Utility function to format numbers with commas
export const formatNumberWithCommas = (value: string | number): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return ''
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

// Utility function to remove commas from input
export const removeCommas = (value: string): string => {
  return value.replace(/,/g, '')
}

// Utility function to calculate total fee (provider fee + platform fee)
export const calculateTotalFee = (
  providerFeeAmount: number,
  sendAmount: number,
  platformFeePercentage: number = 0.6 // 0.6% platform fee
): number => {
  const platformFee = (sendAmount * platformFeePercentage) / 100
  return providerFeeAmount + platformFee
}
